<script setup lang="ts">
import { computed } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import QuoteRequestForm from './QuoteRequestForm.vue'
import type { QuoteBlock, WebsiteLeadCaptureState, WebsiteLeadSubmission } from '../../types'

const props = defineProps<{ block: QuoteBlock; leadCapture?: WebsiteLeadCaptureState }>()
const emit = defineEmits<{ leadSubmit: [submission: WebsiteLeadSubmission] }>()
const effectiveLeadCapture = computed<WebsiteLeadCaptureState>(() => props.leadCapture ?? ({
  enabled: true,
  submitting: false,
  succeeded: false,
}))
</script>

<template>
  <section v-if="block.variant === 'form'" :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container site:grid site:items-start site:gap-10 site:@min-[64rem]:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] site:@min-[64rem]:gap-16">
      <div class="site:pt-3">
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
      </div>
      <div class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-8" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <QuoteRequestForm :content="block.content" :lead-capture="effectiveLeadCapture" @submit="emit('leadSubmit', $event)" />
      </div>
    </div>
  </section>

  <section v-else :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container">
      <div class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-10" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <div class="site:mx-auto site:mb-9 site:max-w-3xl site:text-center">
          <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
          <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
          <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        </div>
        <QuoteRequestForm compact :content="block.content" :lead-capture="effectiveLeadCapture" @submit="emit('leadSubmit', $event)" />
      </div>
    </div>
  </section>
</template>
